import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const InvoicesList = () => {
  // Mock data for invoices - replace with actual data from API/state
  const [invoices] = useState([
    {
      id: 1,
      invoiceNo: 'INV-2026-001',
      customerName: 'Hello Enterprises',
      date: '2026-03-15',
      total: 2500.00,
      status: 'Paid'
    },
    {
      id: 2,
      invoiceNo: 'INV-2026-002',
      customerName: 'Tech Solutions LLC',
      date: '2026-03-16',
      total: 1800.00,
      status: 'Pending'
    },
    {
      id: 3,
      invoiceNo: 'INV-2026-003',
      customerName: 'Global Trading Co',
      date: '2026-03-17',
      total: 3200.00,
      status: 'Overdue'
    },
    {
      id: 4,
      invoiceNo: 'INV-2026-004',
      customerName: 'Modern Services',
      date: '2026-03-18',
      total: 950.00,
      status: 'Paid'
    }
  ]);

  // Individual selection state for each invoice
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());

  // Handle individual invoice selection
  const handleInvoiceSelect = (invoiceId) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedInvoices.size === invoices.length) {
      // If all are selected, deselect all
      setSelectedInvoices(new Set());
    } else {
      // Select all
      setSelectedInvoices(new Set(invoices.map(inv => inv.id)));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'Paid': 'badge-success',
      'Pending': 'badge-warning',
      'Overdue': 'badge-error'
    };
    return `badge ${statusClasses[status] || 'badge-ghost'} badge-sm`;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Invoices</h2>
          <p className="text-sm text-gray-600">Manage all your invoices</p>
        </div>
        <Link to="/invoices/create" className="btn btn-primary btn-sm sm:btn-md w-full sm:w-auto">
          ➕ Create Invoice
        </Link>
      </div>

      {/* Selection Actions */}
      {selectedInvoices.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <span className="text-sm text-blue-800">
              {selectedInvoices.size} invoice{selectedInvoices.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button className="btn btn-sm btn-outline btn-blue flex-1 sm:flex-none">Print Selected</button>
              <button className="btn btn-sm btn-outline btn-green flex-1 sm:flex-none">Email Selected</button>
              <button className="btn btn-sm btn-outline btn-red flex-1 sm:flex-none">Delete Selected</button>
            </div>
          </div>
        </div>
      )}

      {/* Invoices Table - Desktop View */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    checked={selectedInvoices.size === invoices.length && invoices.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Invoice No</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary"
                      checked={selectedInvoices.has(invoice.id)}
                      onChange={() => handleInvoiceSelect(invoice.id)}
                    />
                  </td>
                  <td className="font-medium text-primary">{invoice.invoiceNo}</td>
                  <td>{invoice.customerName}</td>
                  <td>{new Date(invoice.date).toLocaleDateString()}</td>
                  <td className="font-mono">{formatCurrency(invoice.total)}</td>
                  <td>
                    <span className={getStatusBadge(invoice.status)}>
                      {invoice.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      <button className="btn btn-xs btn-ghost">👁️ View</button>
                      <button className="btn btn-xs btn-ghost">📄 PDF</button>
                      <button className="btn btn-xs btn-ghost">✉️ Email</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {invoices.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📄</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h3>
            <p className="text-gray-600 mb-4">Create your first invoice to get started</p>
            <Link to="/invoices/create" className="btn btn-primary">
              Create Your First Invoice
            </Link>
          </div>
        )}
      </div>

      {/* Invoices Cards - Mobile View */}
      <div className="md:hidden space-y-3">
        {invoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📄</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h3>
            <p className="text-gray-600 mb-4">Create your first invoice to get started</p>
            <Link to="/invoices/create" className="btn btn-primary btn-sm">
              Create Your First Invoice
            </Link>
          </div>
        ) : (
          invoices.map((invoice) => (
            <div key={invoice.id} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary checkbox-sm mt-0.5"
                    checked={selectedInvoices.has(invoice.id)}
                    onChange={() => handleInvoiceSelect(invoice.id)}
                  />
                  <div>
                    <p className="font-medium text-primary text-sm">{invoice.invoiceNo}</p>
                    <p className="text-gray-600 text-xs">{invoice.customerName}</p>
                  </div>
                </div>
                <span className={getStatusBadge(invoice.status)}>
                  {invoice.status}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{new Date(invoice.date).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500">Amount</p>
                  <p className="font-medium font-mono">{formatCurrency(invoice.total)}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button className="btn btn-xs btn-ghost flex-1">👁️ View</button>
                <button className="btn btn-xs btn-ghost flex-1">📄 PDF</button>
                <button className="btn btn-xs btn-ghost flex-1">✉️ Email</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default InvoicesList;